"use client";

import { Container } from "@/components/layout/container";
import LinkLoadingIndicator from "@/components/loader/link-loading-indicator";
import { ArrowRight, ChevronDown } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { homeFaqs } from "./home-faq-data";

export function FAQSection() {
  const [activeIndex, setActiveIndex] = useState<number | null>(0);

  const toggleQuestion = (index: number) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  // Two-column masonry split
  const leftColumnFaqs = homeFaqs.filter((_, i) => i % 2 === 0);
  const rightColumnFaqs = homeFaqs.filter((_, i) => i % 2 !== 0);

  const renderFaqItem = (
    faq: (typeof homeFaqs)[0],
    index: number,
    isRightColumn: boolean,
  ) => {
    const actualIndex = isRightColumn ? index * 2 + 1 : index * 2;
    const isOpen = activeIndex === actualIndex;
    const Icon = faq.icon;

    return (
      <div
        key={actualIndex}
        className={[
          "group relative overflow-hidden rounded-2xl border transition-all duration-300",
          "bg-card text-card-foreground",
          isOpen
            ? "border-primary/20 shadow-lg"
            : "border-border/60 hover:border-border hover:shadow-md",
        ].join(" ")}
      >
        <button
          type="button"
          onClick={() => toggleQuestion(actualIndex)}
          className={[
            "w-full flex items-start gap-4 p-5 text-left rounded-2xl transition-colors",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
          ].join(" ")}
          aria-expanded={isOpen}
        >
          {/* Icon Box */}
          <div className="shrink-0">
            <div
              className={[
                "w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 shadow-md ring-2 ring-white/60 dark:ring-slate-800/60",
                isOpen
                  ? `bg-gradient-to-br ${faq.gradient} scale-110`
                  : "bg-slate-100 dark:bg-slate-800 group-hover:scale-110",
              ].join(" ")}
            >
              <Icon
                className={[
                  "w-5 h-5 transition-colors duration-300",
                  isOpen ? "text-white" : "text-slate-600 dark:text-slate-300",
                ].join(" ")}
                strokeWidth={2}
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 pt-1">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 pr-4">
                <span
                  className={[
                    "inline-block text-[10px] font-bold uppercase tracking-widest mb-1.5 transition-colors duration-300",
                    isOpen ? "text-primary" : "text-muted-foreground",
                  ].join(" ")}
                >
                  {faq.category}
                </span>

                <h3
                  className={[
                    "text-base font-semibold leading-tight transition-colors duration-300",
                    isOpen ? "text-foreground" : "text-foreground/90",
                  ].join(" ")}
                >
                  {faq.question}
                </h3>
              </div>

              {/* Toggle Icon */}
              <div
                className={[
                  "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300",
                  isOpen
                    ? "bg-primary/10 text-primary rotate-180"
                    : "text-muted-foreground",
                ].join(" ")}
              >
                <ChevronDown className="w-4 h-4" />
              </div>
            </div>

            {/* Answer */}
            <div
              className="grid transition-all duration-300 ease-[cubic-bezier(0.33,1,0.68,1)]"
              style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
            >
              <div className="overflow-hidden">
                <p
                  className={[
                    "text-sm leading-relaxed pt-3 transition-opacity duration-500 delay-100",
                    "text-muted-foreground",
                    isOpen ? "opacity-100" : "opacity-0",
                  ].join(" ")}
                >
                  {faq.answer}
                </p>
              </div>
            </div>
          </div>
        </button>
      </div>
    );
  };

  return (
    <section id="faq" className="relative overflow-hidden pb-20 pt-10">
      {/* Premium background decorations */}
      <div className="absolute top-20 right-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />

      {/* SVG decorative shapes */}
      <svg
        className="absolute top-16 left-[8%] w-10 h-10 text-primary/15"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <polygon points="12,2 22,12 12,22 2,12" />
      </svg>

      <svg
        className="absolute top-32 right-[5%] w-6 h-6 text-sky-500/20 animate-pulse"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M12,0 L14,10 L24,12 L14,14 L12,24 L10,14 L0,12 L10,10 Z" />
      </svg>

      <svg
        className="absolute bottom-28 right-[10%] w-14 h-14 text-primary/10"
        viewBox="0 0 64 64"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        aria-hidden="true"
      >
        <polygon points="32,4 60,32 32,60 4,32" />
      </svg>

      <svg
        className="absolute bottom-1/3 left-[3%] w-8 h-8 text-violet-500/15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        aria-hidden="true"
      >
        <polygon points="12,2 22,8 22,16 12,22 2,16 2,8" />
      </svg>

      {/* Gradient accent lines */}
      <div className="absolute top-0 right-1/4 w-px h-24 bg-linear-to-b from-primary/15 to-transparent" />
      <div className="absolute bottom-0 left-1/3 w-px h-20 bg-linear-to-t from-sky-500/10 to-transparent" />

      {/* Glowing dots */}
      <div className="absolute top-40 left-[15%] w-2 h-2 bg-primary/30 rounded-full shadow-lg shadow-primary/20" />
      <div className="absolute bottom-32 right-[20%] w-1.5 h-1.5 bg-sky-500/40 rounded-full" />

      <Container className="relative z-10">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between mb-8">
          <div className="max-w-2xl">
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 md:text-5xl dark:text-slate-50">
              Frequently Asked Questions
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-600 md:text-base dark:text-slate-300">
              Find answers to the most common questions about booking flights,
              payments, cancellations, and more.
            </p>
          </div>

          <Link
            href="/faqs"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-900 transition-colors hover:text-primary dark:text-slate-100"
          >
            <LinkLoadingIndicator title="View all FAQs" />
            <ArrowRight className="size-4" />
          </Link>
        </div>

        {/* FAQ List */}
        <div>
          {/* Mobile */}
          <div className="md:hidden space-y-4">
            {homeFaqs.map((faq, i) => renderFaqItem(faq, i, i % 2 !== 0))}
          </div>

          {/* Desktop masonry */}
          <div className="hidden md:grid grid-cols-2 gap-6 items-start">
            <div className="space-y-6">
              {leftColumnFaqs.map((faq, i) => renderFaqItem(faq, i, false))}
            </div>
            <div className="space-y-6">
              {rightColumnFaqs.map((faq, i) => renderFaqItem(faq, i, true))}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

export default FAQSection;
